import { Building2, Network } from "lucide-react";
import { Badge, BadgeVariants } from "~/components/Badge";
import { Emdash } from "~/components/Punctuation";
import { cn } from "~/lib/utils";

// // // //

/**
 * Visual treatments supported by TokenizationSponsorshipBadge
 * - `soft`    - filled categorical colors (indigo = issuer, purple = third-party)
 * - `outline` - the same categorical colors as unfilled borders
 * - `neutral` - equal-weight gray badges with no color coding
 * - `icons`   - gray badges with a relationship icon (default)
 * - `pill`    - the `soft` colors with fully rounded corners
 */
export type SponsorshipBadgeDesign =
    "soft" | "outline" | "neutral" | "icons" | "pill";

/**
 * getTokenizationSponsorshipLabel
 * Maps a raw `tokenization_sponsorship_type` to its human-readable label
 * - `ISSUER`      -> "Issuer-Sponsored"
 * - `THIRD_PARTY` -> "Third-Party"
 *
 * Unrecognized values return `fallback` (defaults to the raw value), so that
 * any new type added to the API still renders something readable.
 *
 * @example
 * getTokenizationSponsorshipLabel("ISSUER"); // "Issuer-Sponsored"
 * getTokenizationSponsorshipLabel("OTHER"); // "OTHER"
 */
export function getTokenizationSponsorshipLabel(
    value: string,
    fallback: string = value,
): string {
    if (value === "ISSUER") return "Issuer-Sponsored";
    if (value === "THIRD_PARTY") return "Third-Party";
    return fallback;
}

/**
 * TokenizationSponsorshipBadge
 * Renders an asset's `tokenization_sponsorship_type` as a Badge
 *
 * Known API values are mapped to human-readable labels via
 * getTokenizationSponsorshipLabel. Unrecognized values render as-is in a gray
 * badge, and a missing value renders an Emdash placeholder.
 *
 * @param props.value - the raw sponsorship type from the API
 * @param props.design - the visual treatment, see SponsorshipBadgeDesign (defaults to "icons")
 * @param props.fullWidth - stretches the badge to fill its container, e.g. a table cell
 *
 * @example
 * <TokenizationSponsorshipBadge value={asset.tokenization_sponsorship_type} />
 */
export function TokenizationSponsorshipBadge({
    value,
    design = "icons",
    fullWidth = false,
}: {
    value: string | null | undefined;
    design?: SponsorshipBadgeDesign;
    fullWidth?: boolean;
}) {
    // Missing sponsorship data renders a placeholder instead of an empty badge
    if (!value) return <Emdash />;

    // Match against the known sponsorship types returned by the API
    const issuer = value === "ISSUER";
    const thirdParty = value === "THIRD_PARTY";

    // Map known types to display labels, falling back to the raw value
    const label = getTokenizationSponsorshipLabel(value);

    // "soft" and "pill" share the same filled colors, and only differ in their
    // border radius (see the `rounded` prop below)
    const colored = design === "soft" || design === "pill";

    // Resolve the Badge color variant:
    // - "soft" / "pill" -> filled indigo (issuer) or purple (third-party)
    // - "outline"       -> indigo or purple outlines
    // - otherwise gray, which covers the "neutral" and "icons" designs, as well
    //   as unrecognized values in every design
    const variant =
        colored && issuer
            ? BadgeVariants.indigo
            : colored && thirdParty
              ? BadgeVariants.purple
              : design === "outline" && issuer
                ? BadgeVariants.indigoOutline
                : design === "outline" && thirdParty
                  ? BadgeVariants.purpleOutline
                  : BadgeVariants.gray;

    // Icon for the "icons" design - a building for the issuer itself, and a
    // network for an external third-party tokenizer
    const Icon = issuer ? Building2 : Network;

    return (
        <Badge
            variant={variant}
            size="sm"
            rounded={design === "pill" ? "full" : "sm"}
            className={cn("gap-1.5 justify-center whitespace-nowrap", {
                "w-full": fullWidth,
            })}
            label={
                <>
                    {/* Only known types get an icon, unrecognized values render as plain text */}
                    {design === "icons" && (issuer || thirdParty) && (
                        <Icon size={12} aria-hidden="true" />
                    )}
                    {label}
                </>
            }
        />
    );
}
